/**
 * Log exclusion for the platform owner: nothing from the owner's account,
 * opted-out browsers or the owner's IP blocks is written to login/lookup logs.
 * Identifiers are stored only as salted FNV-1a hashes.
 */

export const OPT_OUT_KEY = "fd_log_optout";

const SALT = "fd-log-privacy|";

export const EXCLUDED_HASHES = new Set([
  "760191d0", // owner email
  "816f8a56", // owner uid
  "0591fa94", // owner IPv6 /64
  "61a6eb29" // owner IPv4 /24
]);

export function fnv1a(text) {
  let h = 0x811c9dc5;
  const s = String(text);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

const hashOf = (kind, value) => fnv1a(`${SALT}${kind}:${value}`);

function expandIpv6(ip) {
  const [head, tail = ""] = ip.split("::");
  const a = head ? head.split(":") : [];
  const b = ip.includes("::") && tail ? tail.split(":") : [];
  const fill = ip.includes("::") ? Array(Math.max(0, 8 - a.length - b.length)).fill("0") : [];
  return [...a, ...fill, ...b].map((g) => g.replace(/^0+(?=.)/, ""));
}

/** IPv4 → first three octets, IPv6 → first four groups (the /64). */
export function ipBlock(ip) {
  const s = String(ip || "").trim().toLowerCase().split("%")[0];
  if (!s) return null;
  const v4 = s.match(/^(?:::ffff:)?(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.\d{1,3}$/);
  if (v4) return { kind: "ip4", value: `${v4[1]}.${v4[2]}.${v4[3]}` };
  if (s.includes(":")) return { kind: "ip6", value: expandIpv6(s).slice(0, 4).join(":") };
  return null;
}

export function isExcludedIdentity({ uid, email, ip } = {}) {
  if (uid && EXCLUDED_HASHES.has(hashOf("uid", String(uid)))) return true;
  if (email && EXCLUDED_HASHES.has(hashOf("email", String(email).trim().toLowerCase()))) return true;
  const block = ipBlock(ip);
  return !!block && EXCLUDED_HASHES.has(hashOf(block.kind, block.value));
}

function storage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function isBrowserOptedOut() {
  const store = storage();
  try {
    if (typeof window !== "undefined" && /[?&]fd_log_optout=1\b/.test(window.location?.search || "")) {
      store?.setItem(OPT_OUT_KEY, "1");
    }
    return store?.getItem(OPT_OUT_KEY) === "1";
  } catch {
    return false;
  }
}

export function optOutThisBrowser() {
  try {
    storage()?.setItem(OPT_OUT_KEY, "1");
  } catch {
    /* storage unavailable */
  }
}

/** Decide before collecting context; marks the browser when the owner signs in. */
export function shouldSkipBeforeContext({ uid, email } = {}) {
  if (isExcludedIdentity({ uid, email })) {
    optOutThisBrowser();
    return true;
  }
  return isBrowserOptedOut();
}
